import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import referralService from '../services/referralService';
import Swal from 'sweetalert2';
import './Register.css';

function Register() {
    const [step, setStep] = useState(1);
    const [designations, setDesignations] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [partnerCodeReadonly, setPartnerCodeReadonly] = useState(false);
    
    // OTP Resend state
    const [resendTimer, setResendTimer] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [isResending, setIsResending] = useState(false);
    
    // Form data state
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        designation: '',
        partner_ref_code: '',
        partner_id: null,
        password: '',
        password_confirmation: '',
        agree_terms_condition: false
    });

    const [otp, setOtp] = useState('');
    const [captcha, setCaptcha] = useState({ id: '', challenge: '' });
    const [captchaAnswer, setCaptchaAnswer] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    
    const { bootstrapAuth, isAuthenticated, onboardingPath } = useAuth();
    const navigate = useNavigate();

    // Timer countdown effect
    useEffect(() => {
        let interval;
        
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [resendTimer]);

    useEffect(() => {
        fetchDesignations();
        
        // Check for referral data in localStorage
        const referral = referralService.getReferral();
        if (referral) {
            setFormData(prev => ({
                ...prev,
                partner_ref_code: referral.partner_code,
                partner_id: referral.partner_id
            }));
            setPartnerCodeReadonly(true);
        }
        
        if (isAuthenticated && step === 1) {
            navigate(onboardingPath || '/dashboard');
        }
    }, [isAuthenticated, onboardingPath, navigate, step]);

    const fetchDesignations = async () => {
        try {
            const response = await api.get('/designations');
            setDesignations(response.data.data);
        } catch (error) {
            console.error('Failed to fetch designations:', error);
        }
    };

    const fetchCaptcha = async () => {
        try {
            const response = await api.get('/register/captcha');
            const payload = response.data?.data || {};
            setCaptcha({
                id: payload.captcha_id || '',
                challenge: payload.challenge || ''
            });
            setCaptchaAnswer('');
        } catch (error) {
            console.error('Failed to fetch CAPTCHA:', error);
            Swal.fire({
                icon: 'error',
                title: 'CAPTCHA Error',
                text: 'Unable to load CAPTCHA. Please try again.'
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handlePartnerCodeBlur = async () => {
        const code = formData.partner_ref_code.trim();
        
        if (!code || partnerCodeReadonly) {
            return;
        }

        try {
            const response = await api.get(`/partner/verify/${code}`);
            
            if (response.data.success) {
                setFormData(prev => ({
                    ...prev,
                    partner_id: response.data.data.partner_id
                }));

                Swal.fire({
                    icon: 'success',
                    title: 'Valid Partner Code',
                    text: `Partner: ${response.data.data.partner_name || code}`,
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            setFormData(prev => ({
                ...prev,
                partner_id: null
            }));

            Swal.fire({
                icon: 'warning',
                title: 'Invalid Partner Code',
                text: 'The partner code you entered is not valid',
                timer: 2000
            });
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.phone_number.trim()) {
            newErrors.phone_number = 'Phone number is required';
        } else if (formData.phone_number.length !== 10) {
            newErrors.phone_number = 'Phone number must be 10 digits';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
        }
        if (!formData.agree_terms_condition) {
            newErrors.agree_terms_condition = 'You must agree to terms and conditions';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const startResendTimer = (seconds = 60) => {
        setResendTimer(seconds);
        setCanResend(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (!validateStep1()) {
            return;
        }
        
        setLoading(true);
        
        try {
            const registrationData = {
                ...formData,
                partner_id: formData.partner_id
            };

            const response = await api.post('/register', registrationData);
            
            Swal.fire({
                icon: 'success',
                title: 'OTP Sent',
                text: response.data.message,
                timer: 2000
            });

            await fetchCaptcha();
            setStep(2);
            startResendTimer(60); // Start 60 second countdown
        } catch (error) {
            const message = error.response?.data?.message || 'Registration failed';
            const apiErrors = error.response?.data?.errors || {};
            
            setErrors(apiErrors);
            
            Swal.fire({
                icon: 'error',
                title: 'Registration Failed',
                text: message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend || isResending) {
            return;
        }

        setIsResending(true);

        try {
            const response = await api.post('/register/resend-otp', {
                email: formData.email
            });

            Swal.fire({
                icon: 'success',
                title: 'OTP Resent',
                text: response.data.message,
                timer: 2000,
                showConfirmButton: false
            });

            // Start countdown again
            const retryAfter = response.data.retry_after || 60;
            startResendTimer(retryAfter);

        } catch (error) {
            const message = error.response?.data?.message || 'Failed to resend OTP';
            
            // Handle rate limiting (429)
            if (error.response?.status === 429) {
                const retryAfter = error.response?.data?.retry_after || 60;
                startResendTimer(retryAfter);
                
                Swal.fire({
                    icon: 'warning',
                    title: 'Too Many Requests',
                    text: `Please wait ${retryAfter} seconds before requesting again.`
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: message
                });
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        
        if (!otp) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please enter OTP'
            });
            return;
        }

        if (!captchaAnswer.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please solve the CAPTCHA'
            });
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await api.post('/register/verify-otp', {
                email: formData.email,
                otp: otp,
                captcha_id: captcha.id,
                captcha_answer: captchaAnswer
            });
            
            bootstrapAuth(
                response.data.access_token,
                response.data.user || {
                    onboarding: {
                        pending: true,
                        stage: 'complete_registration',
                        path: '/complete-registration'
                    }
                }
            );
            referralService.clearReferral();
            
            Swal.fire({
                icon: 'success',
                title: 'Verified!',
                text: response.data.message,
                timer: 2000
            });
            
            navigate(response.data.user?.onboarding?.path || '/complete-registration');
        } catch (error) {
            const message = error.response?.data?.message || 'OTP verification failed';
            await fetchCaptcha();

            Swal.fire({
                icon: 'error',
                title: 'Verification Failed',
                text: message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        try {
            const response = await api.get('/auth/google');
            window.location.href = response.data.url;
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to initiate Google signup'
            });
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="register-container">
            <div className="register-content">
                <div className="register-form-section">
                    <div className="register-card">
                        <div className="logo-container">
                            <a href="/">
                                <img src="https://cedurnow.com/assets/img/logos/new-logo.png" alt="Logo" width="180" />
                            </a>
                        </div>

                        <h3 className="register-title">Let's get you started</h3>

                        {step === 1 && (
                            <form onSubmit={handleRegister} className="register-form">
                                {/* ... Step 1 form fields (same as before) ... */}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>First Name <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            className="form-control"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            placeholder="First Name"
                                        />
                                        {errors.first_name && (
                                            <span className="error-text">{errors.first_name}</span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Last Name <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            className="form-control"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            placeholder="Last Name"
                                        />
                                        {errors.last_name && (
                                            <span className="error-text">{errors.last_name}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Email <span className="required">*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Email"
                                        />
                                        {errors.email && (
                                            <span className="error-text">{errors.email}</span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Phone Number <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="phone_number"
                                            className="form-control"
                                            value={formData.phone_number}
                                            onChange={handleChange}
                                            placeholder="Phone"
                                            maxLength="10"
                                        />
                                        {errors.phone_number && (
                                            <span className="error-text">{errors.phone_number}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Designation</label>
                                        <select
                                            name="designation"
                                            className="form-control"
                                            value={formData.designation}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Designation</option>
                                            {designations.map(designation => (
                                                <option key={designation.id} value={designation.id}>
                                                    {designation.designation_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Partner Referral Code</label>
                                        <input
                                            type="text"
                                            name="partner_ref_code"
                                            className="form-control"
                                            value={formData.partner_ref_code}
                                            onChange={handleChange}
                                            onBlur={handlePartnerCodeBlur}
                                            placeholder="Partner Referral Code"
                                            readOnly={partnerCodeReadonly}
                                            style={partnerCodeReadonly ? { backgroundColor: '#f0f0f0' } : {}}
                                        />
                                        {partnerCodeReadonly && (
                                            <span className="info-text" style={{ color: '#28a745', fontSize: '12px' }}>
                                                ✓ Referral code applied
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Create Password <span className="required">*</span></label>
                                        <div className="password-input-group">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                className="form-control"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Password"
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <span className="error-text">{errors.password}</span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Confirm Password <span className="required">*</span></label>
                                        <div className="password-input-group">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="password_confirmation"
                                                className="form-control"
                                                value={formData.password_confirmation}
                                                onChange={handleChange}
                                                placeholder="Confirm Password"
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.password_confirmation && (
                                            <span className="error-text">{errors.password_confirmation}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="terms-checkbox">
                                    <input
                                        type="checkbox"
                                        id="agree_terms_condition"
                                        name="agree_terms_condition"
                                        checked={formData.agree_terms_condition}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="agree_terms_condition">
                                        I agree to the <a href="/term-and-condition" target="_blank">Terms of Service and Privacy Policy</a>
                                    </label>
                                </div>
                                {errors.agree_terms_condition && (
                                    <span className="error-text">{errors.agree_terms_condition}</span>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary btn-submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Processing...' : 'Sign Up'}
                                </button>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleVerifyOtp} className="otp-form">
                                <div className="form-group">
                                    <label>Enter OTP <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control otp-input"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        placeholder="Enter 4-digit OTP"
                                        maxLength="4"
                                        autoFocus
                                    />
                                    <p className="otp-info">
                                        An OTP has been sent to <strong>{formData.email}</strong>
                                    </p>
                                </div>

                                <div className="form-group captcha-group">
                                    <label>Security Check <span className="required">*</span></label>
                                    <div className="captcha-box">
                                        <div className="captcha-challenge">
                                            {captcha.challenge || 'Loading...'}
                                        </div>
                                        <button
                                            type="button"
                                            className="captcha-refresh"
                                            onClick={fetchCaptcha}
                                            title="Refresh CAPTCHA"
                                        >
                                            <i className="fa-solid fa-rotate-right"></i>
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={captchaAnswer}
                                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                                        placeholder="Enter CAPTCHA answer"
                                    />
                                    <p className="otp-info">Solve the CAPTCHA before verifying your email.</p>
                                </div>

                                {/* Resend OTP Section */}
                                <div className="resend-otp-section">
                                    {resendTimer > 0 ? (
                                        <p className="resend-timer">
                                            Resend OTP in <strong>{formatTime(resendTimer)}</strong>
                                        </p>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn-resend-otp"
                                            onClick={handleResendOtp}
                                            disabled={!canResend || isResending}
                                        >
                                            {isResending ? (
                                                <>
                                                    <span className="spinner-small"></span>
                                                    Resending...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-rotate-right"></i>
                                                    Resend OTP
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary btn-submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Verifying...' : 'Submit OTP'}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-secondary btn-back"
                                    onClick={() => {
                                        setStep(1);
                                        setCaptcha({ id: '', challenge: '' });
                                        setCaptchaAnswer('');
                                    }}
                                >
                                    Back to Registration
                                </button>
                            </form>
                        )}

                        {step === 1 && (
                            <>
                                <div className="signup-footer">
                                    <p>Already a User? <a href="/login" className="sign-in-link">Login</a></p>
                                </div>

                                <hr className="divider" />

                                <button onClick={handleGoogleSignup} className="btn-google">
                                    <img src="https://cedurnow.com/assets/img/google-signin.png" alt="Sign up with Google" />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="register-info-section">
                    <div className="info-content">
                        <h1>"Create a Cedur account"</h1>
                        <h3>It's free to set up your company with Cedur.</h3>
                        <hr />
                        <p>Not ready to create an account?</p>
                        <a href="https://cedurnow.com/products">Learn how Cedur works</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Register;
